import { Info, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { SettingRow } from '@/components/ui/setting-row';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { useDisclosureSettings } from '@/hooks/useDisclosureSettings';

type PlaceholderKey =
    | 'solutionName'
    | 'solutionPurpose'
    | 'solutionDataSources'
    | 'solutionUserGroups';

type PlaceholderField = {
    key: PlaceholderKey;
    label: string;
    description: string;
    placeholder: string;
    multiline?: boolean;
};

const PLACEHOLDER_FIELDS: PlaceholderField[] = [
    {
        key: 'solutionName',
        label: 'Solution name',
        description:
            'Client, project or solution name the disclosure is written for.',
        placeholder: 'Acme Retail Insights'
    },
    {
        key: 'solutionPurpose',
        label: 'Purpose',
        description:
            'High-level purpose, e.g. data exploration, reporting support, insight generation, performance monitoring, decision support.',
        placeholder: 'reporting support and performance monitoring',
        multiline: true
    },
    {
        key: 'solutionDataSources',
        label: 'Data sources',
        description:
            'The client-specific structured data sources or business data domains the agent is connected to.',
        placeholder:
            'campaign delivery data, CRM segments, and weekly sales exports',
        multiline: true
    },
    {
        key: 'solutionUserGroups',
        label: 'User groups',
        description: 'The primary user groups the solution is introduced for.',
        placeholder: 'marketing analysts and account teams',
        multiline: true
    }
];

const formatSavedAt = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

export const DisclosureTab: React.FC = () => {
    const { settings, loading, save } = useDisclosureSettings();

    const [enabled, setEnabled] = useState(false);
    const [values, setValues] = useState<Record<PlaceholderKey, string>>({
        solutionName: '',
        solutionPurpose: '',
        solutionDataSources: '',
        solutionUserGroups: ''
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!settings) return;
        setEnabled(settings.enabled);
        setValues({
            solutionName: settings.placeholders.solutionName ?? '',
            solutionPurpose: settings.placeholders.solutionPurpose ?? '',
            solutionDataSources:
                settings.placeholders.solutionDataSources ?? '',
            solutionUserGroups: settings.placeholders.solutionUserGroups ?? ''
        });
    }, [settings]);

    const setValue = (key: PlaceholderKey, value: string) =>
        setValues((current) => ({ ...current, [key]: value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await save({
                enabled,
                placeholders: {
                    solutionName: values.solutionName.trim(),
                    solutionPurpose: values.solutionPurpose.trim(),
                    solutionDataSources: values.solutionDataSources.trim(),
                    solutionUserGroups: values.solutionUserGroups.trim()
                }
            });
            toast.success('Disclosure saved');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to save disclosure');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-10 max-w-3xl">
            <section className="space-y-4">
                <SectionHeader
                    title="Disclosure"
                    description="Control whether the transparency disclosure is available to your users"
                />
                <SettingRow
                    label="Show disclosure"
                    description="Makes the Intended Uses Transparency Disclosure readable from the chat footer"
                    htmlFor="disclosure-enabled"
                    wholeRowClickable
                    className="border-b-0"
                >
                    <Switch
                        id="disclosure-enabled"
                        checked={enabled}
                        onCheckedChange={setEnabled}
                        disabled={loading}
                    />
                </SettingRow>
            </section>

            <div className="border-border border-t" />

            <section className="space-y-6">
                <SectionHeader
                    title="Disclosure details"
                    level="section"
                    description="These fill in the customer-specific parts of section 1. Anything left empty is highlighted in the disclosure as not yet filled in."
                />

                <TooltipProvider>
                    <div className="space-y-6">
                        {PLACEHOLDER_FIELDS.map(
                            ({
                                key,
                                label,
                                description,
                                placeholder,
                                multiline
                            }) => (
                                <div key={key} className="space-y-2">
                                    <div className="flex items-center gap-1.5">
                                        <Label
                                            htmlFor={`disclosure-${key}`}
                                            className="text-(length:--fs-subtitle) leading-(--lh-subtitle) text-foreground"
                                        >
                                            {label}
                                        </Label>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon-compact"
                                                    aria-label={`About ${label}`}
                                                    className="rounded-sm w-5 h-5"
                                                >
                                                    <Info />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-xs">
                                                {description}
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                    {multiline ? (
                                        <Textarea
                                            id={`disclosure-${key}`}
                                            value={values[key]}
                                            onChange={(e) =>
                                                setValue(key, e.target.value)
                                            }
                                            placeholder={placeholder}
                                            disabled={loading}
                                            rows={3}
                                            className="resize-y"
                                        />
                                    ) : (
                                        <Input
                                            id={`disclosure-${key}`}
                                            value={values[key]}
                                            onChange={(e) =>
                                                setValue(key, e.target.value)
                                            }
                                            placeholder={placeholder}
                                            disabled={loading}
                                           
                                        />
                                    )}
                                </div>
                            )
                        )}
                    </div>
                </TooltipProvider>

                <div className="flex items-center gap-3 pt-2">
                    <Button
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        {saving ? (
                            <>
                                <Loader2 className="mr-2 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save disclosure'
                        )}
                    </Button>
                    {settings?.updatedAt && (
                        <span className="text-body text-muted-foreground">
                            Last edited {formatSavedAt(settings.updatedAt)}
                        </span>
                    )}
                </div>
            </section>
        </div>
    );
};
